/**
 * Who is signed in to one sign-in MCP connection, and revoking a person. The
 * admin's side of per-person sign-in; the person's own side is
 * settings/useConnectedAccountsLogic.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { McpSignInUser } from '../../lib/types.js';

export interface McpSignInsLogic {
  users: McpSignInUser[];
  isLoading: boolean;
  error: Error | null;
  revoke: (userId: string) => Promise<void>;
  revokingId: string | null;
}

export const useMcpSignInsLogic = (
  tenantId: string | undefined,
  integrationId: string | undefined,
  enabled = true,
): McpSignInsLogic => {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.mcpSignIns(tenantId ?? '', integrationId ?? '');
  const query = useQuery({
    queryKey,
    enabled: enabled && Boolean(tenantId) && Boolean(integrationId),
    queryFn: () =>
      apiRequest<{ users: McpSignInUser[] }>(
        `/api/integrations/${integrationId ?? ''}/mcp-sign-ins`,
      ),
  });
  const revoke = useMutation<void, Error, string>({
    mutationFn: (userId) =>
      apiRequest<void>(`/api/integrations/${integrationId ?? ''}/mcp-sign-ins/${userId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });
  return {
    users: query.data?.users ?? [],
    isLoading: query.isLoading,
    error: query.error ?? revoke.error,
    revoke: (userId) => revoke.mutateAsync(userId),
    revokingId: revoke.isPending ? revoke.variables : null,
  };
};
